import type { RefObject } from 'react';
import { useEffect } from 'react';

import {
  AUTOFIT_EDGE_TOLERANCE,
  AUTOFIT_MAX_WIDTH,
  AUTOFIT_MIN_WIDTH,
  computeAutofitWidth,
  isWithinColumnEdge,
} from './columnAutofit';

const RESIZER_SELECTOR = 'button[class*="resizer"]';
const HEADER_CONTENT_SELECTOR = '[class*="header-cell-content"]';
const BODY_CELL_CONTENT_SELECTOR = '[class*="body-cell-content"]';

// Cap the number of rows measured per fit so very large pages stay responsive.
// Only the currently rendered page is measured regardless.
const MAX_MEASURED_ROWS = 500;

interface UseColumnAutofitOptions {
  /** When false the behaviour is inert (e.g. non-resizable tables). Defaults to true. */
  enabled?: boolean;
  maxWidth?: number;
  minWidth?: number;
}

/**
 * Measure an element's true natural (un-truncated) content width, independent of its
 * current rendered width. Cloudscape body cells use `table-layout: fixed` and apply cell
 * padding via ancestor-scoped CSS, so a bare clone loses that padding and `scrollWidth`
 * can never report less than the current (fixed) width. We clone the node and copy its
 * computed padding + font inline with `box-sizing: content-box; width: auto`, which lets
 * the clone collapse to its content and report a width the column can both grow and
 * shrink to.
 */
const measureNaturalWidth = (
  element: HTMLElement | null,
  ruler: HTMLElement
): number => {
  if (!element) {
    return 0;
  }
  const computed = getComputedStyle(element);
  // cloneNode is typed as returning Node; a deep clone of an HTMLElement is an HTMLElement.
  const clone = element.cloneNode(true) as HTMLElement;
  clone.removeAttribute('style');
  Object.assign(clone.style, {
    boxSizing: 'content-box',
    width: 'auto',
    maxWidth: 'none',
    minWidth: '0',
    whiteSpace: 'nowrap',
    font: computed.font,
    fontFamily: computed.fontFamily,
    fontSize: computed.fontSize,
    fontWeight: computed.fontWeight,
    letterSpacing: computed.letterSpacing,
    paddingLeft: computed.paddingLeft,
    paddingRight: computed.paddingRight,
  });
  ruler.replaceChildren(clone);

  return Math.ceil(clone.getBoundingClientRect().width);
};

const createRuler = (doc: Document): HTMLElement => {
  const ruler = doc.createElement('div');
  Object.assign(ruler.style, {
    position: 'fixed',
    top: '-9999px',
    left: '-9999px',
    visibility: 'hidden',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    display: 'inline-block',
  });
  doc.body.appendChild(ruler);

  return ruler;
};

interface ResizeTarget {
  table: HTMLTableElement;
  th: HTMLTableCellElement;
}

/**
 * Resolve the column a pointer event is targeting for resize: either the resize handle
 * directly under the pointer, or a column whose right border the pointer is within
 * `tolerance` px of (Excel-style border double-click).
 */
const resolveResizeTarget = (
  event: MouseEvent,
  root: HTMLElement,
  tolerance: number
): ResizeTarget | null => {
  const target = event.target;
  if (!(target instanceof Element) || !root.contains(target)) {
    return null;
  }

  const handle = target.closest(RESIZER_SELECTOR);
  if (handle) {
    const th = handle.closest('th');
    const table = th?.closest('table');
    if (th && table?.tHead && table.tBodies[0]) {
      return { table, th };
    }
  }

  const table = target.closest('table');
  // Cloudscape's sticky-header variant renders a separate body <table> whose <thead>
  // has no rows (the header cells live in the header table), so guard the header row.
  // `rows?.[0]` (not `rows[0]`) because some DOM impls return an undefined `rows`.
  const headerRow = table?.tHead?.rows?.[0];
  if (!table || !headerRow || !table.tBodies[0]) {
    return null;
  }
  for (const headerCell of Array.from(headerRow.cells)) {
    const rect = headerCell.getBoundingClientRect();
    if (
      event.clientY >= rect.top &&
      event.clientY <= rect.bottom &&
      isWithinColumnEdge(event.clientX, rect.right, tolerance) &&
      headerCell.querySelector(RESIZER_SELECTOR)
    ) {
      return { table, th: headerCell };
    }
  }

  return null;
};

const measureColumnWidth = (
  table: HTMLTableElement,
  columnIndex: number,
  options: { minWidth: number; maxWidth: number }
): number => {
  const ruler = createRuler(table.ownerDocument);
  try {
    const headerCell = table.tHead?.rows?.[0]?.cells?.[columnIndex] ?? null;
    const headerContent =
      headerCell?.querySelector<HTMLElement>(HEADER_CONTENT_SELECTOR) ??
      headerCell;
    const headerNatural = measureNaturalWidth(headerContent, ruler);

    const cellNaturals: number[] = [];
    const rows = table.tBodies[0]?.rows;
    if (rows) {
      const rowCount = Math.min(rows.length, MAX_MEASURED_ROWS);
      for (let i = 0; i < rowCount; i++) {
        const cell = rows[i]?.cells?.[columnIndex];
        if (!cell) {
          continue;
        }
        const content =
          cell.querySelector<HTMLElement>(BODY_CELL_CONTENT_SELECTOR) ?? cell;
        cellNaturals.push(measureNaturalWidth(content, ruler));
      }
    }

    return computeAutofitWidth(headerNatural, cellNaturals, options);
  } finally {
    ruler.remove();
  }
};

const nextFrame = (win: Window): Promise<void> =>
  new Promise((resolve) => win.requestAnimationFrame(() => resolve()));

const dispatchMouse = (
  target: Document | Element,
  type: string,
  clientX: number,
  clientY: number,
  view: Window | null
): void => {
  target.dispatchEvent(
    new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      view,
      button: 0,
      clientX,
      clientY,
    })
  );
};

/**
 * Resize the column to `width` by driving Cloudscape's own resize handle. A bare
 * `onColumnWidthsChange` would not take effect live (Cloudscape only seeds its internal
 * width map at mount), so we simulate the drag the handle expects: the resize updates the
 * live width and Cloudscape persists it through its existing change handler.
 */
const resizeColumn = async (
  table: HTMLTableElement,
  th: HTMLTableCellElement,
  width: number
): Promise<void> => {
  const win = table.ownerDocument.defaultView;
  const handle = th.querySelector(RESIZER_SELECTOR);
  if (!win || !handle) {
    return;
  }
  const headerRect = th.getBoundingClientRect();
  const handleRect = handle.getBoundingClientRect();
  const y = headerRect.top + headerRect.height / 2;
  const targetX = Math.round(headerRect.left + width - win.scrollX);

  dispatchMouse(handle, 'mousedown', handleRect.left + 1, y, win);
  // Let Cloudscape commit the drag-start state and attach its move/up listeners.
  await nextFrame(win);
  await nextFrame(win);
  dispatchMouse(table.ownerDocument, 'mousemove', targetX, y, win);
  dispatchMouse(table.ownerDocument, 'mouseup', targetX, y, win);

  // Clear the keyboard-resize focus state the constituent clicks may have left behind.
  const active = table.ownerDocument.activeElement;
  if (active instanceof HTMLElement) {
    active.blur();
  }
};

/**
 * Enable Excel-style double-click-to-auto-fit on the resizable columns of the table(s)
 * rendered inside `rootRef`. Double-clicking a column's right border (or its resize
 * handle) resizes that column to fit its widest visible content, clamped to
 * `[minWidth, maxWidth]`, and suppresses the sort that the clicks would otherwise trigger.
 */
export const useColumnAutofit = (
  rootRef: RefObject<HTMLElement | null>,
  {
    enabled = true,
    maxWidth = AUTOFIT_MAX_WIDTH,
    minWidth = AUTOFIT_MIN_WIDTH,
  }: UseColumnAutofitOptions = {}
): void => {
  useEffect(() => {
    const root = rootRef.current;
    if (!enabled || !root) {
      return;
    }
    const doc = root.ownerDocument;

    // Swallow the clicks that make up a double-click in the resize zone so they don't
    // toggle the column sort. Capture phase + a containment guard keeps this scoped to
    // this table while still firing before Cloudscape's bubble-phase sort handler.
    // Clicks on the resize handle itself are left alone — they never sort, and Cloudscape
    // uses them to enter keyboard-resize mode (accessibility), so suppressing them would
    // be a regression.
    const onClick = (event: MouseEvent): void => {
      const target = event.target;
      if (target instanceof Element && target.closest(RESIZER_SELECTOR)) {
        return;
      }
      if (resolveResizeTarget(event, root, AUTOFIT_EDGE_TOLERANCE)) {
        event.stopPropagation();
      }
    };

    const onDoubleClick = (event: MouseEvent): void => {
      const resolved = resolveResizeTarget(event, root, AUTOFIT_EDGE_TOLERANCE);
      if (!resolved) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const { table, th } = resolved;
      const headerRow = table.tHead?.rows?.[0];
      if (!headerRow) {
        return;
      }
      const columnIndex = Array.from(headerRow.cells).indexOf(th);
      if (columnIndex < 0) {
        return;
      }
      const width = measureColumnWidth(table, columnIndex, {
        minWidth,
        maxWidth,
      });
      void resizeColumn(table, th, width);
    };

    doc.addEventListener('click', onClick, true);
    doc.addEventListener('dblclick', onDoubleClick, true);

    return () => {
      doc.removeEventListener('click', onClick, true);
      doc.removeEventListener('dblclick', onDoubleClick, true);
    };
  }, [rootRef, enabled, maxWidth, minWidth]);
};
